import React, { useEffect, useState } from "react";
import classNames from "classnames";
import "./index.scss";

import { todayAnchor, periodRange, navAnchor, ymd, parseYmd, addDays } from "../../../utils/analytics-period";
import { isRTL } from "../../../services/userModel";
import { DEFAULT_PERIOD_STATE } from "../../../utils/constants";
import ApPeriodPicker from "../analytics/ApPeriodPicker";
import { useSelector } from "react-redux";

// The analytics-dashboard period switch (Day | Week | Month | Custom + ◀ label ▶) as a drop-in
// replacement for CustomerReviewSearchPeriod on CustomTable pages. Emits the same filter shape the
// pages already consume: { start: Date, end: Date, periodFilter: {} } with an inclusive end-of-day end.
const AnalyticsPeriodControl = ({ filter, onSetFilter, t }) => {
  const isLoading = useSelector(state => state.sectionsWrapperData.isLoading);
  const customTableConfigPagination = useSelector(state => state.order.data.customTableConfigPagination.customerService?.loading);

  const [grain, setGrain] = useState(filter?.periodFilter.day
    ? "day"
    : filter?.periodFilter.week
      ? "week"
      : filter?.periodFilter.month
        ? "month"
        : filter?.periodFilter.custom
          ? "custom"
          : "day");
  const [anchor, setAnchor] = useState(todayAnchor());

  useEffect(() => {
    if(!Object.keys(filter?.periodFilter).length) {
      setGrain("");
      return;
    }
    setGrain(filter?.periodFilter.day
      ? "day"
      : filter?.periodFilter.week
        ? "week"
        : filter?.periodFilter.month
          ? "month"
          : filter?.periodFilter.custom
            ? "custom"
            : "day");
  }, [filter.periodFilter]);

  useEffect(() => {
    if (!filter?.start) return;
    const d = new Date(filter.start);
    setAnchor(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())));
  }, [filter.start]);

  const GRAINS = ["day", "week", "month"];

  // half-open [from, to) ymd range → local Date pair (start 00:00, end 23:59:59.999 of the last day)
  const emit = (fromYmd, toYmd, grain) => {
    const f = parseYmd(fromYmd), tEx = parseYmd(toYmd);
    const start = new Date(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate(), 0, 0, 0, 0);
    const last = addDays(tEx, -1);
    const end = new Date(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate(), 23, 59, 59, 999);
    onSetFilter({ start, end, periodFilter: { ...DEFAULT_PERIOD_STATE, [grain]: true } });
  };

  const applyGrain = (g, a) => {
    const r = periodRange(g, a);
    emit(r.from, r.to, g);
  };

  const pickGrain = (g) => {
    setGrain(g);
    if (g === "custom") return;   // keep the current dates; the pickers below take over
    const a = todayAnchor();
    setAnchor(a);
    applyGrain(g, a);
  };

  // No future in orders (same rule as analytics): the "next" nav is disabled once the shown
  // period already reaches today (its exclusive end is past today); custom pickers cap at today.
  const todayYmd = ymd(todayAnchor());
  const nextDisabled = grain !== "custom" && periodRange(grain, anchor).to > todayYmd;

  const nav = (dir) => {
    if (dir > 0 && nextDisabled) return;
    const a = navAnchor(grain, anchor, dir);
    setAnchor(a);
    applyGrain(grain, a);
  };

  const toLocalYmd = (d) => {
    if (!d) return "";
    const x = new Date(d);
    const p = (n) => String(n).padStart(2, "0");
    return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
  };
  const onCustom = (fromStr, toStr) => {
    if (!fromStr || !toStr) return;
    emit(fromStr, ymd(addDays(parseYmd(toStr), 1)), 'custom');   // inclusive picker end → half-open
  };

  const label = grain === "custom" ? "" : periodRange(grain, anchor).label;
  const rtl = isRTL();

  const onChangeDate = (d) => {
    const dates = periodRange(grain, d);
    applyGrain(grain, grain === "month" ? new Date(dates.to) : new Date(dates.from));
  }

  return (
    <div className="apcPeriod">
      <span className="apc-seg">
        {GRAINS.map(g => (
          <button key={g} type="button" className={classNames({ active: grain === g })}
            onClick={() => pickGrain(g)}>{t("analytics." + g.toUpperCase())}</button>
        ))}
        <button type="button" className={classNames({ active: grain === "custom" })}
          onClick={() => pickGrain("custom")}>{t("analytics.CUSTOM")}</button>
      </span>

      {!!grain && grain !== "custom" && (
        <span className="apc-nav">
          <button type="button" disabled={isLoading || customTableConfigPagination} onClick={() => nav(-1)}>{rtl ? "▶" : "◀"}</button>
          <ApPeriodPicker granularity={grain} anchor={anchor} label={label} onPick={d => onChangeDate(d)} />
          <button type="button" onClick={() => nav(1)} disabled={nextDisabled || isLoading || customTableConfigPagination}>{rtl ? "◀" : "▶"}</button>
        </span>
      )}
      {grain === "custom" && (
        <span className="apc-custom">
          <input type="date" value={toLocalYmd(filter && filter.start)} max={todayYmd}
            onChange={e => onCustom(e.target.value, toLocalYmd(filter && filter.end) || e.target.value)} />
          <input type="date" value={toLocalYmd(filter && filter.end)} max={todayYmd}
            onChange={e => onCustom(toLocalYmd(filter && filter.start) || e.target.value, e.target.value)} />
        </span>
      )}
    </div>
  );
};

export default AnalyticsPeriodControl;
